import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Colors } from '@/common/colors/colors';
import SelectInput from '@/common/components/SelectInput';
import moment from 'moment';
import { useRequestsContext } from '@/common/hooks/requestHook';
import { CONSULTANT_ROLES } from '@/pages/login/components/LoginForm/loginForm';
import { getHoursForCCManager } from '@/API/repositories/consultantHours';
import Loading from '@/common/components/Loading';
import CSV from '@/common/components/CSV';
import { useCommonDataContext } from '@/common/hooks/commonDataContext';
import Input from '@/common/components/Input';
import ActionButton from '@/common/components/buttons/ActionButton';
import {
	Wrapper,
	SearchBar,
	SearchItem,
	SearchButtons,
} from './hoursReport.styles';
import { HEADERS } from '../../../common/constants/headersHoursReport';
import HoursReportTable from './components/HoursReportTable';
import { getCSVBodyForHoursReport } from './helpers/getCSVBodyForHoursReport';
import ExportExcel from '@/common/components/ExportExcel';
import { getExcelBodyForHoursReport } from '../../../common/functions/getExcelBodyForHoursRepor';
import { COMPANIES_OPTIONS } from '@/common/constants/Accounting';

export default function HoursReport() {
	const [selectedCompany, setSelectedCompany] = useState(COMPANIES_OPTIONS[0]);

	const {
		options: { queuesOptions },
		filterUsersByRoles,
		queryValues: { isLoading },
		commonData: { users },
	} = useCommonDataContext();

	const [selectedQueues, setSelectedQueues] = useState(queuesOptions);
	const [filterRegex, setFilterRegex] = useState();
	const [allData, setAllData] = useState([]);
	const [consultants, setConsultants] = useState();
	const [selectedConsultant, setSelectedConsultants] = useState();
	const dateFromRef = useRef();
	const dateToRef = useRef();

	const data = useMemo(() => {
		if (selectedCompany && allData) {
			return [...allData].filter(
				(ad) => ad._user.company === selectedCompany.value,
			);
		}

		return allData;
	}, [selectedCompany, allData]);

	const { hasUnfilledRequest, makeRequest } = useRequestsContext();

	const handleGetConsultants = async () => {
		const consultantResponseValidated = filterUsersByRoles(CONSULTANT_ROLES);

		setConsultants(() => consultantResponseValidated);
		setSelectedConsultants(() => consultantResponseValidated);
	};

	const handleSearch = async (e) => {
		e && e.preventDefault();

		setAllData(() => null);

		const payload = {};

		payload.from = dateFromRef.current.value;
		payload.to = dateToRef.current.value;
		payload.monthFormated = moment
			.utc(dateFromRef.current.value)
			.format('MMMM_YYYY')
			.toUpperCase();

		payload.accepted = true;

		const response = await makeRequest(
			getHoursForCCManager.bind(null, payload),
		);

		if (response.data) {
			setAllData(() => response.data);

			response.data.forEach((hours, i) => {
				const foundTicket = hours.data.some((hour) => hour.ticket);

				response.data[i].foundTicket = foundTicket;
			});
		}
	};

	const handleSelectQueue = (queue) => {
		let selectedQueueUsers = [];

		queue.forEach((q) => {
			selectedQueueUsers = [...selectedQueueUsers, ...q.value.consultants];
		});

		const users = consultants.filter((c) =>
			selectedQueueUsers.includes(c.value),
		);

		setSelectedConsultants(() => users);
		setSelectedQueues(() => queue);
	};

	const handleChangeRegex = (e) => {
		if (e.target.value.length >= 1) {
			return setFilterRegex(() => e.target.value);
		}

		return setFilterRegex(() => null);
	};

	useEffect(() => {
		handleGetConsultants();
		if (queuesOptions) {
			setSelectedQueues(() => queuesOptions);
		}
	}, [users, queuesOptions]);

	return (
		<div>
			<Wrapper>
				{(isLoading || hasUnfilledRequest(getHoursForCCManager)) && <Loading />}
				<SearchBar>
					<SearchItem>
						<Input
							name='From'
							width={80}
							color={Colors.darkBlue}
							inputRef={dateFromRef}
							value={moment().startOf('month').format('YYYY-MM-DD')}
							type='date'
							inputWidth={140}
							hasIncDec
							required
							requiredSign={true}
						/>
						<Input
							name='To'
							width={80}
							color={Colors.darkBlue}
							inputRef={dateToRef}
							value={moment().endOf('month').format('YYYY-MM-DD')}
							type='date'
							inputWidth={140}
							hasIncDec
							required
							requiredSign={true}
						/>
					</SearchItem>
					<SearchItem>
						<SelectInput
							name='Comapny'
							options={COMPANIES_OPTIONS}
							color={Colors.darkBlue}
							width={80}
							selectWidth={120}
							setSelected={setSelectedCompany}
							selected={selectedCompany}
						/>
						<Input
							width={80}
							inputWidth={120}
							onChange={(e) => handleChangeRegex(e)}
							name='Includer'
							color={Colors.darkBlue}
						/>
					</SearchItem>
					<SelectInput
						showLabel={false}
						selectWidth={1000}
						options={consultants}
						selected={selectedConsultant}
						setSelected={setSelectedConsultants}
						multiple={true}
						placeholder={'Select consultants...'}
					/>
					<SelectInput
						showLabel={false}
						placeholder={'Select queues...'}
						selectWidth={1000}
						options={queuesOptions}
						setSelected={handleSelectQueue}
						selected={selectedQueues}
						multiple={true}
					/>
					<SearchButtons>
						{data && data.length > 0 && (
							<>
								<ExportExcel
									label='pl'
									excelData={getExcelBodyForHoursReport(
										data,
										selectedConsultant,
										consultants,
										dateFromRef.current.value,
										dateToRef.current.value,
										filterRegex,
										HEADERS.PL,
									)}
									fileName={`hours_report_pl_${dateFromRef.current.value}_${dateToRef.current.value}_${selectedCompany.value}`}
								/>
								<ExportExcel
									label='en'
									excelData={getExcelBodyForHoursReport(
										data,
										selectedConsultant,
										consultants,
										dateFromRef.current.value,
										dateToRef.current.value,
										filterRegex,
										HEADERS.EN,
									)}
									fileName={`hours_report_en_${dateFromRef.current.value}_${dateToRef.current.value}_${selectedCompany.value}`}
								/>
							</>
						)}

						{data && data.length > 0 && (
							<CSV
								label='pl'
								header={HEADERS.PL}
								data={getCSVBodyForHoursReport(
									data,
									selectedConsultant,
									consultants,
									dateFromRef.current.value,
									dateToRef.current.value,
									filterRegex,
								)}
								filename={`Hours report PL ${dateFromRef.current.value}_${dateToRef.current.value}`}
							/>
						)}
						{data && data.length > 0 && (
							<CSV
								label='en'
								header={HEADERS.EN}
								data={getCSVBodyForHoursReport(
									data,
									selectedConsultant,
									consultants,
									dateFromRef.current.value,
									dateToRef.current.value,
									filterRegex,
								)}
								filename={`Hours report EN ${dateFromRef.current.value}_${dateToRef.current.value}`}
							/>
						)}
						<ActionButton
							defaultText={'Add all'}
							onClick={() => setSelectedConsultants(consultants)}
						/>
						<ActionButton
							defaultText='Search'
							onClick={(e) => handleSearch(e)}
						/>
					</SearchButtons>
				</SearchBar>
				{!!data?.length && (
					<HoursReportTable
						data={data}
						filterRegex={filterRegex}
						selectedConsultant={selectedConsultant}
						consultants={consultants}
					/>
				)}
			</Wrapper>
		</div>
	);
}
